import { TFunction } from 'i18next';

import { BOOK_NAME_TO_CODE } from '@/constants/bookCodes';

export const wordToNumberMap: Record<string, number> = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  thirteen: 13,
  fourteen: 14,
  fifteen: 15,
  sixteen: 16,
  seventeen: 17,
  eighteen: 18,
  nineteen: 19,
};

const tensToNumberMap: Record<string, number> = {
  twenty: 20,
  thirty: 30,
  forty: 40,
  fifty: 50,
  sixty: 60,
  seventy: 70,
  eighty: 80,
  ninety: 90,
};

const capitalize = (word: string) => word.charAt(0).toUpperCase() + word.slice(1);

const splitModuleName = (rawModuleName: string) => {
  const [numberSegment, bookSegment = ''] = rawModuleName.split('--');
  const numberParts = numberSegment.split('-');
  const bookParts = bookSegment ? bookSegment.split('-') : [];

  return { numberParts, bookParts };
};

const parseModuleNumberParts = (parts: string[]) => {
  const [first = '', second = ''] = parts;
  const firstWord = first.toLowerCase();
  const secondWord = second.toLowerCase();

  const directMatch = wordToNumberMap[firstWord];
  if (directMatch !== undefined) {
    return { moduleNum: String(directMatch), consumedParts: 1 };
  }

  const tensMatch = tensToNumberMap[firstWord];
  if (tensMatch !== undefined) {
    const unitMatch = wordToNumberMap[secondWord];
    if (unitMatch !== undefined && unitMatch < 10) {
      return { moduleNum: String(tensMatch + unitMatch), consumedParts: 2 };
    }

    return { moduleNum: String(tensMatch), consumedParts: 1 };
  }

  return { moduleNum: first, consumedParts: 1 };
};

const buildBookKey = (bookParts: string[]) => {
  const leadingBookNumber = wordToNumberMap[bookParts[0]?.toLowerCase() ?? ''];

  if (leadingBookNumber !== undefined && leadingBookNumber < 10) {
    return `${leadingBookNumber} ${bookParts.slice(1).map(capitalize).join(' ')}`;
  }

  return bookParts.map(capitalize).join(' ');
};

export const translateModuleName = (t: TFunction, rawModuleName: string) => {
  const { numberParts, bookParts: explicitBookParts } = splitModuleName(rawModuleName);
  const { moduleNum, consumedParts } = parseModuleNumberParts(numberParts.slice(1));
  const bookParts = explicitBookParts.length > 0
    ? explicitBookParts
    : numberParts.slice(1 + consumedParts);
  const bookKey = buildBookKey(bookParts);

  const assignmentKey = bookParts.join('-').toLowerCase();
  const assignmentNameTranslation = t(`assignment-names.${assignmentKey}`, { defaultValue: '' });

  const moduleName = assignmentNameTranslation || (bookKey
    ? t(`bookList.${bookKey}`, { defaultValue: bookKey })
    : '');

  return t('modules.module-number-name', {
    module: t('modules.module'),
    moduleNum,
    moduleName,
  }).trim();
};

export const getBookCodeFromModuleName = (moduleName: string): string | null => {
  const { numberParts, bookParts: explicitBookParts } = splitModuleName(moduleName);
  if (numberParts.length < 2) return null;

  const { consumedParts } = parseModuleNumberParts(numberParts.slice(1));
  const bookParts = explicitBookParts.length > 0
    ? explicitBookParts
    : numberParts.slice(1 + consumedParts);
  if (bookParts.length === 0) return null;
  const bookKey = buildBookKey(bookParts);

  return BOOK_NAME_TO_CODE[bookKey] ?? null;
};

export const getModuleName = (
  t: TFunction,
  module: string,
  levelId: number,
  targetLanguage?: string,
  role?: string,
) => {
  const formattedModuleName = translateModuleName(t, module);

  if (levelId !== 1) {
    return formattedModuleName;
  }

  const assignmentNames = {
    biblicalVocabulary: t('assignment-names.biblical-vocabulary'),
    writingStyles: t('assignment-names.writing-styles'),
    foundations: t('assignment-names.foundations'),
    machineTranslationReview: t('assignment-names.machine-translation-review'),
  };

  const modules: Record<string, Record<string, string>> = {
    Contributor: {
      'module-one': `${formattedModuleName} ${targetLanguage} ${assignmentNames.biblicalVocabulary}`,
      'module-two': `${formattedModuleName} ${targetLanguage} ${assignmentNames.writingStyles}`,
    },
    'QA Lead': {
      'module-one': `${formattedModuleName} ${targetLanguage} ${assignmentNames.foundations}`,
      'module-two': `${formattedModuleName} ${assignmentNames.machineTranslationReview}`,
    },
  };

  const defaultModules: Record<string, string> = {
    'module-one': `${formattedModuleName} ${targetLanguage} ${assignmentNames.biblicalVocabulary}`,
    'module-two': `${formattedModuleName} ${targetLanguage} ${assignmentNames.writingStyles}`,
  };

  return modules[role ?? '']?.[module] || defaultModules[module] || formattedModuleName;
};
